import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderSlot as _renderSlot, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, createSlots as _createSlots, unref as _unref, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "data-table__reorder-bar"
}
const _hoisted_2 = {
  key: 0,
  class: "data-table__pagination-info"
}
const _hoisted_3 = {
  key: 0,
  class: "data-table__pagination-info__selected"
}
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "data-table__pagination-info__value" }
const _hoisted_6 = { class: "data-table__pagination-info__value" }
const _hoisted_7 = { class: "data-table__reorder-bar-actions" }
const _hoisted_8 = {
  key: 1,
  class: "data-table__bulk-bar"
}
const _hoisted_9 = { class: "data-table__bulk-bar-left" }
const _hoisted_10 = ["checked", "indeterminate"]
const _hoisted_11 = { class: "data-table__bulk-bar-count" }
const _hoisted_12 = { class: "data-table__bulk-bar-actions" }
const _hoisted_13 = { class: "data-table__wrapper" }
const _hoisted_14 = { class: "data-table__table" }
const _hoisted_15 = { class: "data-table__header-row" }
const _hoisted_16 = {
  key: 0,
  class: "data-table__header-cell data-table__header-cell--checkbox data-table__header-cell--sticky"
}
const _hoisted_17 = ["checked", "indeterminate"]
const _hoisted_18 = {
  key: 1,
  class: "data-table__header-cell data-table__header-cell--actions"
}
const _hoisted_19 = { class: "data-table__body" }
const _hoisted_20 = {
  key: 0,
  class: "data-table__cell data-table__cell--checkbox"
}
const _hoisted_21 = {
  key: 1,
  class: "data-table__cell data-table__cell--actions"
}
const _hoisted_22 = { key: 1 }
const _hoisted_23 = ["colspan"]
const _hoisted_24 = {
  key: 0,
  class: "data-table__cell data-table__cell--checkbox data-table__cell--sticky"
}
const _hoisted_25 = {
  key: 1,
  class: "data-table__empty-value"
}
const _hoisted_26 = { class: "data-table__cell-content" }
const _hoisted_27 = { class: "data-table__tooltip" }
const _hoisted_28 = { key: 3 }
const _hoisted_29 = {
  key: 1,
  class: "data-table__cell data-table__cell--actions"
}
const _hoisted_30 = {
  key: 2,
  class: "data-table__pagination-wrapper"
}

import { ref, computed, watch, h, defineComponent } from "vue";
import { Icon } from "@iconify/vue";
import Checkbox from "@/components/ui/Checkbox.vue";
import ColumnHeader from "./ColumnHeader.vue";
import ColumnReorder from "./ColumnReorder.vue";
import TablePagination from "./TablePagination.vue";
import TableEmptyState from "./TableEmptyState.vue";
import ActionDropdown from "./ActionDropdown.vue";
import type {
  TableColumn,
  TableAction,
  PaginationState,
  ColumnState,
  SortState,
  FilterState,
  SortDirection,
} from "./types";

interface Props<T extends { id: string | number } = any> {
  data: T[];
  columns: TableColumn<T>[];
  actions?: TableAction<T>[] | ((row: T) => TableAction<T>[]);
  selectable?: boolean;
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
  emptyState?: {
    title?: string;
    description?: string;
    action?: any;
    icon?: string;
  };
  pagination?: PaginationState;
  onPaginationChange?: (pagination: Partial<PaginationState>) => void;
  reorderable?: boolean;
  stickyColumns?: number;
  onSort?: (column: string, direction: SortDirection) => void;
  onFilter?: (filters: FilterState) => void;
  perPageOptions?: number[];
  showPaginationInfo?: boolean;
  selectedCount?: number;
  initialSelectedIds?: (string | number)[];
  loading?: boolean;
  storageKey?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DataTable',
  props: {
    data: {},
    columns: {},
    actions: {},
    selectable: { type: Boolean, default: false },
    onSelectionChange: {},
    emptyState: {},
    pagination: {},
    onPaginationChange: {},
    reorderable: { type: Boolean, default: false },
    stickyColumns: { default: 1 },
    onSort: {},
    onFilter: {},
    perPageOptions: { default: () => [50, 100, 200] },
    showPaginationInfo: { type: Boolean, default: false },
    selectedCount: { default: 0 },
    initialSelectedIds: { default: () => [] },
    loading: { type: Boolean, default: false },
    storageKey: { default: undefined }
  },
  setup(__props: any) {

const RenderCell = defineComponent({
  props: {
    renderFn: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    return () => {
      const result = props.renderFn();
      if (result && typeof result === "object" && "type" in result) {
        return result;
      }
      if (typeof result === "string" || typeof result === "number") {
        return h("span", String(result));
      }
      return h("span", String(result ?? ""));
    };
  },
});

const props = __props;

const selectedIds = ref<Set<string | number>>(new Set());
const hasAppliedInitialSelection = ref(false);

function buildColumnOrder(): ColumnState[] {
  const defaults: ColumnState[] = props.columns.map((col) => ({
    key: String(col.key),
    header: col.header,
    visible: col.visible !== false,
  }));
  if (!props.storageKey) return defaults;
  try {
    const raw = localStorage.getItem(props.storageKey);
    if (!raw) return defaults;
    const saved: ColumnState[] = JSON.parse(raw);
    // Keep saved order/visibility; drop removed columns; append new ones
    const merged = saved
      .filter((s) => defaults.some((d) => d.key === s.key))
      .map((s) => {
        const current = defaults.find((d) => d.key === s.key)!;
        return { key: current.key, header: current.header, visible: s.visible };
      });
    defaults.forEach((d) => {
      if (!merged.some((m) => m.key === d.key)) merged.push(d);
    });
    return merged;
  } catch {
    return defaults;
  }
}

const columnOrder = ref<ColumnState[]>(buildColumnOrder());

watch(
  () => props.storageKey,
  () => {
    columnOrder.value = buildColumnOrder();
  }
);

watch(
  columnOrder,
  (newOrder) => {
    if (props.storageKey) {
      localStorage.setItem(props.storageKey, JSON.stringify(newOrder));
    }
  },
  { deep: true }
);
const sortState = ref<SortState>({ column: null, direction: null });
const filterState = ref<FilterState>({});

const allSelected = computed(() => {
  return (
    filteredData.value.length > 0 &&
    filteredData.value.every((row) => selectedIds.value.has(row.id))
  );
});

const someSelected = computed(() => {
  return filteredData.value.some((row) => selectedIds.value.has(row.id)) && !allSelected.value;
});

const orderedColumns = computed(() => {
  return columnOrder.value
    .filter((col) => col.visible)
    .map((col) => props.columns.find((c) => String(c.key) === col.key))
    .filter(Boolean) as TableColumn[];
});

const totalColumns = computed(() => {
  let count = orderedColumns.value.length;
  if (props.selectable) count++;
  if (props.actions && props.actions.length > 0) count++;
  return count;
});

const isEmpty = computed(() => filteredData.value.length === 0);

const getRowKey = (row: any, index: number): string | number => {
  return row.id ?? index;
};

const getCellValue = (row: any, key: string | number | symbol): any => {
  if (typeof key === "string" && key.includes(".")) {
    return key.split(".").reduce((obj, k) => obj?.[k], row);
  }
  return row[key];
};

const getColumnStyle = (column: TableColumn): Record<string, string> => {
  const style: Record<string, string> = {};
  if (column.width) style.width = column.width;
  if (column.maxWidth) style.maxWidth = column.maxWidth;
  if (column.minWidth) style.minWidth = column.minWidth;
  return style;
};

const getStickyStyle = (colIndex: number): Record<string, string> => {
  if (colIndex >= props.stickyColumns) return {};
  const checkboxOffset = props.selectable ? 36 : 0;
  let left = checkboxOffset;
  for (let i = 0; i < colIndex; i++) {
    const col = orderedColumns.value[i];
    if (col?.width) {
      left += parseInt(col.width, 10) || 0;
    } else if (col?.minWidth) {
      left += parseInt(col.minWidth, 10) || 0;
    } else {
      left += 150;
    }
  }
  return { position: "sticky", left: `${left}px`, zIndex: "2" };
};

const getHeaderStickyStyle = (colIndex: number): Record<string, string> => {
  const base = getStickyStyle(colIndex);
  if (!base.position) return {};
  return { ...base, top: "0px", zIndex: "4" };
};

const isEmptyValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const isRowSelected = (row: any): boolean => {
  return selectedIds.value.has(row.id);
};

const handleSelectAll = (checked: boolean) => {
  if (checked) {
    filteredData.value.forEach((row) => selectedIds.value.add(row.id));
  } else {
    filteredData.value.forEach((row) => selectedIds.value.delete(row.id));
  }
  props.onSelectionChange?.(Array.from(selectedIds.value));
};

const handleSelectRow = (row: any, checked: boolean) => {
  if (checked) {
    selectedIds.value.add(row.id);
  } else {
    selectedIds.value.delete(row.id);
  }
  props.onSelectionChange?.(Array.from(selectedIds.value));
};

const handleColumnReorder = (newOrder: ColumnState[]) => {
  columnOrder.value = newOrder;
};

const handleColumnVisibilityChange = (key: string, visible: boolean) => {
  columnOrder.value = columnOrder.value.map((col) => (col.key === key ? { ...col, visible } : col));
};

const handleSort = (columnKey: string, direction: SortDirection) => {
  sortState.value = { column: direction ? columnKey : null, direction };
  props.onSort?.(columnKey, direction);
};

const handleFilter = (columnKey: string, values: string[]) => {
  const newFilters = { ...filterState.value, [columnKey]: values };
  if (values.length === 0) {
    delete newFilters[columnKey];
  }
  filterState.value = newFilters;
  props.onFilter?.(newFilters);
};

const filteredData = computed(() => {
  let result = [...props.data];

  Object.entries(filterState.value).forEach(([columnKey, filterValues]) => {
    if (filterValues.length === 0) return;

    const column = props.columns.find((col) => String(col.key) === columnKey);
    if (!column) return;

    if (column.filterType === "multi-select" || !column.filterType) {
      result = result.filter((row) => {
        const value = getCellValue(row, column.key);

        if (Array.isArray(value)) {
          const arrayValues = value.map((item: any) => {
            if (item && typeof item === "object") {
              return String(item.id || item.tagId || item.value || item);
            }
            return String(item ?? "");
          });
          return filterValues.some((filterValue) =>
            arrayValues.some((arrVal) => String(arrVal).trim() === String(filterValue).trim())
          );
        }

        if (value && typeof value === "object") {
          const objId = value.id || value.tagId || value.value;
          if (objId !== undefined) {
            return filterValues.includes(String(objId));
          }
          const objStr = JSON.stringify(value);
          return filterValues.some((filterValue) => objStr.includes(String(filterValue)));
        }

        const strValue = String(value ?? "").trim();
        return filterValues.some((filterValue) => strValue === String(filterValue).trim());
      });
    }
  });

  if (sortState.value.column && sortState.value.direction) {
    const sortColumn = props.columns.find((col) => String(col.key) === sortState.value.column);
    if (sortColumn) {
      result.sort((a, b) => {
        const aValue = getCellValue(a, sortColumn.key);
        const bValue = getCellValue(b, sortColumn.key);
        const sortType = sortColumn.sortType || "alphabetic";

        let comparison = 0;

        if (sortType === "date") {
          const aDate = aValue ? new Date(aValue).getTime() : Number.NEGATIVE_INFINITY;
          const bDate = bValue ? new Date(bValue).getTime() : Number.NEGATIVE_INFINITY;
          const aValid = !isNaN(aDate) && isFinite(aDate);
          const bValid = !isNaN(bDate) && isFinite(bDate);

          if (!aValid && !bValid) comparison = 0;
          else if (!aValid) comparison = 1;
          else if (!bValid) comparison = -1;
          else comparison = aDate - bDate;
        } else if (sortType === "numeric") {
          const aNum = Number(aValue) || 0;
          const bNum = Number(bValue) || 0;
          comparison = aNum - bNum;
        } else {
          const aStr = String(aValue ?? "").toLowerCase();
          const bStr = String(bValue ?? "").toLowerCase();
          comparison = aStr.localeCompare(bStr);
        }

        return sortState.value.direction === "asc" ? comparison : -comparison;
      });
    }
  }

  return result;
});

watch(
  () => props.columns,
  (newColumns) => {
    const existingKeys = new Set(columnOrder.value.map((col) => col.key));
    const newKeys = new Set(newColumns.map((col) => String(col.key)));

    newColumns.forEach((col) => {
      const key = String(col.key);
      if (!existingKeys.has(key)) {
        columnOrder.value.push({
          key,
          header: col.header,
          visible: col.visible !== false,
        });
      } else {
        const existing = columnOrder.value.find((c) => c.key === key);
        if (existing) {
          existing.header = col.header;
        }
      }
    });

    columnOrder.value = columnOrder.value.filter((col) => newKeys.has(col.key));
  },
  { deep: true }
);

watch(
  () => props.data,
  () => {
    hasAppliedInitialSelection.value = false;
    // Sync selectedIds from initialSelectedIds to restore checkmarks on page navigation
    if (props.initialSelectedIds?.length) {
      const idsSet = new Set(props.initialSelectedIds.map(String));
      selectedIds.value = new Set(
        (props.data || [])
          .filter((row: any) => row.id != null && idsSet.has(String(row.id)))
          .map((row: any) => row.id)
      );
      hasAppliedInitialSelection.value = true;
    } else {
      selectedIds.value = new Set();
    }
  }
);

watch(
  [filteredData, () => props.initialSelectedIds],
  ([data, ids]) => {
    if (!data?.length || !ids?.length || hasAppliedInitialSelection.value || !props.selectable) {
      return;
    }
    const idsSet = new Set((ids || []).map((id) => String(id)));
    const toSelect: (string | number)[] = data
      .filter((row: any) => row.id != null && idsSet.has(String(row.id)))
      .map((row: any) => row.id);
    if (toSelect.length) {
      selectedIds.value = new Set(toSelect);
      props.onSelectionChange?.(Array.from(selectedIds.value));
      hasAppliedInitialSelection.value = true;
    }
  },
  { flush: "post", immediate: true }
);

return (_ctx: any,_cache: any) => {
  const _component_VTooltip = _resolveComponent("VTooltip")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['data-table', { 'data-table--selectable': __props.selectable }])
  }, [
    (__props.reorderable || __props.showPaginationInfo)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (__props.showPaginationInfo)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                (__props.selectedCount && __props.selectedCount > 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(__props.selectedCount) + " selected ", 1))
                  : (__props.pagination)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                        _cache[4] || (_cache[4] = _createTextVNode(" Showing ", -1)),
                        _createElementVNode("span", _hoisted_5, _toDisplayString(__props.pagination.page * __props.pagination.perPage <= __props.pagination.total
              ? __props.pagination.page * __props.pagination.perPage
              : __props.pagination.total), 1),
                        _cache[5] || (_cache[5] = _createTextVNode(" from ", -1)),
                        _createElementVNode("span", _hoisted_6, _toDisplayString(__props.pagination.total), 1),
                        _cache[6] || (_cache[6] = _createTextVNode(" results ", -1))
                      ]))
                    : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_7, [
            _renderSlot(_ctx.$slots, "toolbar-actions"),
            (__props.reorderable)
              ? (_openBlock(), _createBlock(ColumnReorder, {
                  key: 0,
                  columns: columnOrder.value,
                  "sticky-columns": __props.stickyColumns,
                  onReorder: handleColumnReorder,
                  onVisibilityChange: handleColumnVisibilityChange
                }, null, 8, ["columns", "sticky-columns"]))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    (selectedIds.value.size > 0 && _ctx.$slots['bulk-actions'])
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("input", {
              type: "checkbox",
              checked: allSelected.value,
              indeterminate: someSelected.value && !allSelected.value,
              onChange: _cache[0] || (_cache[0] = (e) => handleSelectAll((e.target as HTMLInputElement).checked)),
              class: "data-table__checkbox"
            }, null, 40, _hoisted_10),
            _createElementVNode("span", _hoisted_11, _toDisplayString(selectedIds.value.size) + " selected", 1)
          ]),
          _createElementVNode("div", _hoisted_12, [
            _renderSlot(_ctx.$slots, "bulk-actions", {
              selectedIds: Array.from(selectedIds.value),
              count: selectedIds.value.size
            })
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_13, [
      _createElementVNode("table", _hoisted_14, [
        _createElementVNode("thead", null, [
          _createElementVNode("tr", _hoisted_15, [
            (__props.selectable)
              ? (_openBlock(), _createElementBlock("th", _hoisted_16, [
                  _createElementVNode("input", {
                    type: "checkbox",
                    checked: allSelected.value,
                    indeterminate: someSelected.value && !allSelected.value,
                    onChange: _cache[1] || (_cache[1] = (e) => handleSelectAll((e.target as HTMLInputElement).checked)),
                    class: "data-table__checkbox"
                  }, null, 40, _hoisted_17)
                ]))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(orderedColumns.value, (column, colIndex) => {
              return (_openBlock(), _createElementBlock("th", {
                key: String(column.key),
                class: _normalizeClass([
                'data-table__header-cell',
                {
                  'data-table__header-cell--sticky-col': colIndex < __props.stickyColumns,
                  'data-table__header-cell--sticky-col-last': colIndex === __props.stickyColumns - 1,
                },
              ]),
                style: _normalizeStyle({ ...getColumnStyle(column), ...getHeaderStickyStyle(colIndex) })
              }, [
                _createVNode(ColumnHeader, {
                  label: column.header,
                  sortable: column.sortable,
                  "sort-direction": 
                  sortState.value.column === String(column.key) ? sortState.value.direction : null
                ,
                  filterable: column.filterable,
                  "filter-type": column.filterType,
                  "filter-options": column.filterOptions,
                  "active-filters": filterState.value[String(column.key)] || [],
                  onSort: (direction) => handleSort(String(column.key), direction),
                  onFilter: (values) => handleFilter(String(column.key), values)
                }, null, 8, ["label", "sortable", "sort-direction", "filterable", "filter-type", "filter-options", "active-filters", "onSort", "onFilter"])
              ], 6))
            }), 128)),
            (__props.actions)
              ? (_openBlock(), _createElementBlock("th", _hoisted_18, [...(_cache[7] || (_cache[7] = [
                  _createElementVNode("span", { class: "sr-only" }, "Actions", -1)
                ]))]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("tbody", _hoisted_19, [
          (__props.loading)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, _renderList(5, (n) => {
                return _createElementVNode("tr", {
                  key: 'skeleton-' + n,
                  class: "data-table__row"
                }, [
                  (__props.selectable)
                    ? (_openBlock(), _createElementBlock("td", _hoisted_20, [...(_cache[8] || (_cache[8] = [
                        _createElementVNode("div", {
                          class: "data-table__skeleton",
                          style: {"width":"16px","height":"16px"}
                        }, null, -1)
                      ]))]))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(orderedColumns.value, (column, colIndex) => {
                    return (_openBlock(), _createElementBlock("td", {
                      key: String(column.key),
                      class: "data-table__cell",
                      style: _normalizeStyle(getColumnStyle(column))
                    }, [
                      _createElementVNode("div", {
                        class: "data-table__skeleton",
                        style: _normalizeStyle({ width: 60 + ((colIndex * 13) % 40) + '%' })
                      }, null, 4)
                    ], 4))
                  }), 128)),
                  (__props.actions)
                    ? (_openBlock(), _createElementBlock("td", _hoisted_21, [...(_cache[9] || (_cache[9] = [
                        _createElementVNode("div", {
                          class: "data-table__skeleton",
                          style: {"width":"20px"}
                        }, null, -1)
                      ]))]))
                    : _createCommentVNode("", true)
                ])
              }), 64))
            : (isEmpty.value)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_22, [
                  _createElementVNode("td", {
                    colspan: totalColumns.value,
                    class: "data-table__empty-cell"
                  }, [
                    _createVNode(TableEmptyState, {
                      icon: __props.emptyState?.icon,
                      title: __props.emptyState?.title,
                      description: __props.emptyState?.description
                    }, _createSlots({ _: 2 }, [
                      (__props.emptyState?.action)
                        ? {
                            name: "action",
                            fn: _withCtx(() => [
                              (_openBlock(), _createBlock(_resolveDynamicComponent(__props.emptyState.action)))
                            ]),
                            key: "0"
                          }
                        : undefined
                    ]), 1032, ["icon", "title", "description"])
                  ], 8, _hoisted_23)
                ]))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(filteredData.value, (row, rowIndex) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: getRowKey(row, rowIndex),
                    class: _normalizeClass([
              'data-table__row',
              {
                'data-table__row--selected': isRowSelected(row),
              },
            ])
                  }, [
                    (__props.selectable)
                      ? (_openBlock(), _createElementBlock("td", _hoisted_24, [
                          _createVNode(Checkbox, {
                            "model-value": isRowSelected(row),
                            "onUpdate:modelValue": (val) => handleSelectRow(row, val),
                            size: "sm"
                          }, null, 8, ["model-value", "onUpdate:modelValue"])
                        ]))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(orderedColumns.value, (column, colIndex) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: String(column.key),
                        class: _normalizeClass([
                'data-table__cell',
                {
                  'data-table__cell--truncate': column.truncate !== false,
                  'data-table__cell--bold': column.bold,
                  'data-table__cell--sticky-col': colIndex < __props.stickyColumns,
                  'data-table__cell--sticky-col-last': colIndex === __props.stickyColumns - 1,
                },
              ]),
                        style: _normalizeStyle({ ...getColumnStyle(column), ...getStickyStyle(colIndex) })
                      }, [
                        _renderSlot(_ctx.$slots, `cell(${String(column.key)})`, {
                          row: row,
                          value: getCellValue(row, column.key),
                          column: column
                        }, () => [
                          (column.render)
                            ? (_openBlock(), _createBlock(_unref(RenderCell), {
                                key: 0,
                                "render-fn": () => column.render!(row, rowIndex)
                              }, null, 8, ["render-fn"]))
                            : (isEmptyValue(getCellValue(row, column.key)))
                              ? (_openBlock(), _createElementBlock("span", _hoisted_25, [...(_cache[10] || (_cache[10] = [
                                  _createElementVNode("span", { class: "data-table__empty-dash" }, null, -1)
                                ]))]))
                              : (column.showTooltip)
                                ? (_openBlock(), _createBlock(_component_VTooltip, { key: 2 }, {
                                    popper: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_27, _toDisplayString(String(getCellValue(row, column.key) ?? "")), 1)
                                    ]),
                                    default: _withCtx(() => [
                                      _createElementVNode("span", _hoisted_26, _toDisplayString(String(getCellValue(row, column.key) ?? "")), 1)
                                    ]),
                                    _: 2
                                  }, 1024))
                                : (_openBlock(), _createElementBlock("span", _hoisted_28, _toDisplayString(String(getCellValue(row, column.key))), 1))
                        ])
                      ], 6))
                    }), 128)),
                    (__props.actions)
                      ? (_openBlock(), _createElementBlock("td", _hoisted_29, [
                          _createVNode(ActionDropdown, {
                            row: row,
                            actions: typeof __props.actions === 'function' ? __props.actions(row) : __props.actions
                          }, null, 8, ["row", "actions"])
                        ]))
                      : _createCommentVNode("", true)
                  ], 2))
                }), 128))
        ])
      ])
    ]),
    (__props.pagination && __props.onPaginationChange)
      ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
          _createVNode(TablePagination, {
            pagination: __props.pagination,
            "per-page-options": __props.perPageOptions,
            onPageChange: _cache[2] || (_cache[2] = (page) => __props.onPaginationChange?.({ page })),
            onPerPageChange: _cache[3] || (_cache[3] = (perPage) => __props.onPaginationChange?.({ perPage, page: 1 }))
          }, null, 8, ["pagination", "per-page-options"])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})